import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Container from '@cloudscape-design/components/container';
import Toggle from '@cloudscape-design/components/toggle';
import TokenGroup from '@cloudscape-design/components/token-group';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import { createStore, CreateStoreInput } from '../api/stores';

export function CreateStore() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [deletionProtection, setDeletionProtection] = useState(true);
  const [streamKeyField, setStreamKeyField] = useState('');
  const [streamKeyFields, setStreamKeyFields] = useState<string[]>([]);
  const [delimiter, setDelimiter] = useState('#');
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [errors, setErrors] = useState<{ name?: string; domain?: string }>({});

  const mutation = useMutation({
    mutationFn: () => {
      const input: CreateStoreInput = { name, domain };
      if (streamKeyFields.length > 0) {
        input.streamKey = { fields: streamKeyFields, delimiter };
      }
      input.deletionProtection = deletionProtection;
      return createStore(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      navigate('/stores');
    },
    onError: (err: Error) => {
      setFlash([{
        type: 'error',
        content: err.message,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }]);
    },
  });

  const handleSubmit = () => {
    const newErrors: { name?: string; domain?: string } = {};
    if (!name.trim()) newErrors.name = 'Store name is required.';
    // Must match backend regex in stores.ts
    else if (!/^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(name)) newErrors.name = 'Must be 3–64 chars, lowercase alphanumeric and hyphens.';
    if (!domain.trim()) newErrors.domain = 'Domain is required.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) mutation.mutate();
  };

  const addStreamKeyField = () => {
    const trimmed = streamKeyField.trim();
    if (trimmed && !streamKeyFields.includes(trimmed)) {
      setStreamKeyFields([...streamKeyFields, trimmed]);
      setStreamKeyField('');
    }
  };

  return (
    <SpaceBetween size="l">
      <Flashbar items={flash} />
      <Header variant="h1">Create event store</Header>

      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate('/')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              onClick={handleSubmit}
            >
              Create store
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Store identity</Header>}>
            <ColumnLayout columns={2}>
              <FormField
                label="Store name"
                description="Unique identifier for this event store. Immutable after creation."
                constraintText="Lowercase letters, numbers, and hyphens. 3–64 characters."
                errorText={errors.name}
              >
                <Input
                  value={name}
                  onChange={({ detail }) => setName(detail.value)}
                  placeholder="orders"
                />
              </FormField>
              <FormField
                label="Domain"
                description="Business domain this store belongs to. Used for organizing and filtering."
                errorText={errors.domain}
              >
                <Input
                  value={domain}
                  onChange={({ detail }) => setDomain(detail.value)}
                  placeholder="ecommerce"
                />
              </FormField>
            </ColumnLayout>
          </Container>

          <Container header={<Header variant="h2">Stream key composition</Header>}>
            <SpaceBetween size="m">
              <Box color="text-body-secondary">
                Define how stream identities are derived from event fields. When configured,
                the service composes stream IDs from the specified fields rather than requiring
                callers to provide opaque stream identifiers.
              </Box>
              <FormField
                label="Key fields"
                description="Fields from event data used to compose the stream identity. Order matters."
              >
                <SpaceBetween size="xs" direction="horizontal">
                  <Input
                    value={streamKeyField}
                    onChange={({ detail }) => setStreamKeyField(detail.value)}
                    placeholder="customerId"
                    onKeyDown={({ detail }) => {
                      if (detail.key === 'Enter') addStreamKeyField();
                    }}
                  />
                  <Button onClick={addStreamKeyField} disabled={!streamKeyField.trim()}>
                    Add
                  </Button>
                </SpaceBetween>
              </FormField>
              {streamKeyFields.length > 0 && (
                <TokenGroup
                  items={streamKeyFields.map(f => ({ label: f, dismissLabel: `Remove ${f}` }))}
                  onDismiss={({ detail }) => {
                    setStreamKeyFields(streamKeyFields.filter((_, i) => i !== detail.itemIndex));
                  }}
                />
              )}
              {streamKeyFields.length > 1 && (
                <FormField
                  label="Delimiter"
                  description="Character used to join key fields into a stream ID."
                >
                  <Input
                    value={delimiter}
                    onChange={({ detail }) => setDelimiter(detail.value)}
                    placeholder="#"
                  />
                </FormField>
              )}
              {streamKeyFields.length > 0 && (
                <Box color="text-status-info" fontSize="body-s">
                  Preview: events with {streamKeyFields.map(f => <code key={f}>{f}</code>).reduce((a, b) => <>{a} + {b}</>)} →
                  stream ID <code>{streamKeyFields.map(f => `{${f}}`).join(delimiter)}</code>
                </Box>
              )}
            </SpaceBetween>
          </Container>

          <ExpandableSection headerText="Protection settings" variant="footer">
            <FormField
              label="Deletion protection"
              description="When enabled, the store cannot be deleted without first disabling this setting."
            >
              <Toggle
                checked={deletionProtection}
                onChange={({ detail }) => setDeletionProtection(detail.checked)}
              >
                {deletionProtection ? 'Enabled' : 'Disabled'}
              </Toggle>
            </FormField>
          </ExpandableSection>
        </SpaceBetween>
      </Form>
    </SpaceBetween>
  );
}
